import { Link as RouterLink } from "react-router-dom";
import { Link, Tooltip, Typography, Chip, CircularProgress } from "@mui/material";
import { fDateTime, yearFirstDateTimeFormat } from "src/utils/formatTime";
import { CustomerActionMenu } from "./CustomerActionMenu";
import CustomerTagActionMenu from "./CustomerTagActionMenu";
import IconButton from "@mui/material/IconButton";
import { Icon } from "@iconify/react";

const dateFormat = "dd-MM-yyyy";

export const PROMO_CODE_NOTES = {
  SMS_INITIALISED: 'Promo code assigned and notified via SMS',
  EMAIL_INITIALISED: 'Promo code assigned and notified via EMAIL',
}

export const PROMO_CODE_AUDIT_STATUS = {
  PROCESS: 'PROCESS',
  SENT: 'SENT',
  FAILED: 'FAILED',
}

export const userListColumns = (isReadOnly, companyType) => {
  const columns = [
    {
      field: "fullName",
      headerName: "Name",
      flex: 0.9,
      minWidth: 150,
      valueGetter: (params) => `${params?.row?.firstName ? params?.row?.firstName : ""} ${params?.row?.lastName ? params?.row?.lastName : ""}`
    },
    {
      field: "email",
      headerName: "Email ID",
      flex: 1.2,
      minWidth: 250,
      renderCell: ({ value, id, row }) => (
        <>
          {isReadOnly ? value : <Link underline="none" component={RouterLink} to={`${id}/company/${row?.companyId}`}>
            {value}
          </Link>}
        </>
      ),
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
      minWidth: 180,
      renderCell: ({ value, id, row }) => (
        <>
          {isReadOnly ? value : <Link underline="none" component={RouterLink} to={`${id}/company/${row?.companyId}`}>
            {value}
          </Link>}
        </>
      ),
    },
    {
      field: "balanceAmount",
      headerName: "Wallet Balance",
      flex: 1,
      minWidth: 120
    },
  ];
  if (companyType === 'GP') {
    columns.push({
      field: "companyName",
      headerName: "Company Name",
      flex: 1,
      minWidth: 120
    });
  }
  columns.push(
    {
      field: "active",
      headerName: "Status",
      flex: 1,
      renderCell: ({ value }) => {
        let statusColor = value === "N" ? "red" : "#00AB55";
        let status = value === "N" ? "Disabled" : "Enabled";
        return (
          <span style={{ marginLeft: "10px", color: statusColor }}>{status}</span>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created on",
      type: "date",
      flex: 1,
      minWidth: 200,
      valueFormatter: ({ value }) => {
        return fDateTime(value, dateFormat);
      },
    },
    {
      field: "Actions",
      width: 70,
      sortable: false,
      renderCell: CustomerActionMenu,
    }
  );
  return columns;
};

export const cmWalletHistoryColumns = (isReadOnly) => ([
  {
    headerName: "Sr. No.",
    field: "id",
    flex: 0.9,
    minWidth: 100,
  },
  {
    field: "payment_type",
    headerName: "Payment Type",
    flex: 1.2,
    minWidth: 200,
  },
  {
    field: "payment_method",
    headerName: "Payment Method",
    flex: 1.2,
    minWidth: 200,
  },
  {
    field: "payment_on",
    headerName: "Payment On",
    type: "date",
    flex: 1,
    minWidth: 200,
    valueFormatter: ({ value }) => {
      return value ? fDateTime(value, "dd-MM-yyyy hh:mm:ss a") : "-";
    },
  },
  {
    field: "remarks",
    headerName: "Remarks",
    flex: 1.5,
    minWidth: 220,
    valueFormatter: ({ value }) => value || "-",
  },
  {
    field: "ticket_id",
    headerName: "Ticket ID",
    flex: 1,
    minWidth: 160,
    valueFormatter: ({ value }) => value || "-",
  },
  {
    field: "done_by",
    headerName: "Done By",
    flex: 1,
    minWidth: 170,
    valueFormatter: ({ value }) => value || "-",
  },
  {
    field: "ip_address",
    headerName: "IP Address",
    flex: 1,
    minWidth: 160,
    valueFormatter: ({ value }) => value || "-",
  },
  {
    field: "debit",
    headerName: "Debit",
    flex: 1.2,
    minWidth: 100,
    renderCell: ({ value }) => {
      return (
        <span style={{ marginLeft: "10px", color: "red" }}>{value === 0 ? '' : value}</span>
      );
    },
  },
  {
    field: "credit",
    headerName: "Credit",
    flex: 1.2,
    minWidth: 100,
    renderCell: ({ value }) => {
      return (
        <span style={{ marginLeft: "10px", color: "#00AB55" }}>{value === 0 ? '' : value}</span>
      );
    },
  },
]);

export const cmTransactionsListColumns = (isReadOnly, id, downloadInvoice, isGeneratingPDF = false, currentDownloadingId = null) => ([
  {
    field: "transactionId",
    headerName: "Transaction ID",
    flex: 0.7,
    minWidth: 150,
    renderCell: ({ value, id: tId }) => (
      <>
        {isReadOnly ? value : (
          <Link underline="none" component={RouterLink} to={`/transactions/${tId}?source=cm-customer/${id}`}>
            {value}
          </Link>
        )}
      </>
    ),
  },
  {
    field: "chargepoint_id",
    headerName: "ChargeBox ID",
    flex: 0.7,
    minWidth: 120,
  },
  {
    field: "payment_type",
    headerName: "Payment Type",
    flex: 0.5,
    minWidth: 130,
  },
  {
    field: "totalAmountPaid",
    headerName: "Amount Paid",
    flex: 0.6,
    minWidth: 130,
  },
  {
    field: "totalAmountCharged",
    headerName: "Charging Cost",
    flex: 0.6,
    minWidth: 140,
  },
  {
    field: "invoice",
    headerName: "Invoice",
    flex: 0.8,
    minWidth: 160,
    renderCell: ({ value, row }) => {
      const isCurrentRowLoading = isGeneratingPDF && currentDownloadingId === row.transactionId;
      return (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Tooltip title={isCurrentRowLoading ? "Generating PDF..." : "Download Invoice"}>
            <span>
              <IconButton
                onClick={() => downloadInvoice(row)}
                size="small"
                disabled={isGeneratingPDF}
                sx={{
                  color: isCurrentRowLoading ? 'primary.main' : 'inherit',
                }}
              >
                {isCurrentRowLoading ? (
                  <CircularProgress size={16} thickness={4} color="primary" />
                ) : (
                  <Icon icon="eva:download-fill" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      );
    },
  },
]);

export const getCustomerDefaultValues = (teamId) => ({
  companyId: teamId,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  locationLatitude: "",
  locationLongitude: "",
  street: "",
  house_number: "",
  zipCode: "",
  state: "",
  country: "",
  // ocppTagId: ""
});

export const customerFormTabs = [
  { value: "general", label: "GENERAL" },
  { value: "address", label: "ADDRESS" },
  { value: "tags", label: "RFID TAGS" },
];

export const cmCustomerTabList = [
  {
    value: "customer_info",
    label: "Customer Info",
  },
  {
    value: "wallet_history",
    label: "Wallet History",
  },
  {
    value: "transaction_history",
    label: "Transaction History",
  },
  {
    value: "sent_promo_codes",
    label: "Sent Codes",
  },
  {
    value: "used_promo_codes",
    label: "Used Codes",
  }
];

export const hideForTabs = ["tags"];


export const customerTagFormColumns = (refetch, removeTagFn) => [
  {
    field: "tag",
    headerName: "RFID Tag",
    flex: 1,
    renderCell: ({ value, row: { ocpp_tag_pk } }) => {
      return (
        <Link underline="none" component={RouterLink} to={`/tag/${ocpp_tag_pk}`}>
          {value}
        </Link>
      )
    },
  },
  {
    field: "parenttag",
    headerName: "Parent Group",
    flex: 1,
  },
  {
    field: "expirydate",
    headerName: "Expiry Date/Time",
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
        <span>{yearFirstDateTimeFormat(value)}</span>
      </Tooltip>
    )
  },
  {
    field: "in_transaction",
    headerName: "In Transactions",
    type: "boolean",
    flex: 1,
  },
  {
    field: "blocked",
    headerName: "Blocked",
    type: "boolean",
    flex: 1,
  },
  {
    field: "Actions",
    width: 70,
    sortable: false,
    renderCell: (props) => <CustomerTagActionMenu {...props} refetch={refetch} removeTagFn={removeTagFn} />,
  },
];

// Promo Codes Sent to Customer columns
export const sentPromoCodesColumns = (isReadOnly) => [
  {
    field: "code",
    headerName: "Code",
    flex: 0.8,
    minWidth: 120,
    renderCell: ({ value }) => (
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#04aa58' }}>
        {value}
      </Typography>
    )
  },
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "discount_type",
    headerName: "Discount Type",
    flex: 0.8,
    minWidth: 120,
  },
  {
    field: "discount_value",
    headerName: "Discount",
    flex: 0.7,
    minWidth: 100,
    renderCell: (params) => {
      const { value, row } = params;
      const suffix = row.discount_type === "PERCENTAGE" ? `${value}%` : `${value}`;

      return (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#04aa58' }}>
          {suffix}
        </Typography>
      );
    }
  },
  {
    field: "audit_status",
    headerName: "Status",
    flex: 0.8,
    minWidth: 100,
    renderCell: ({ value }) => {
      const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
          case PROMO_CODE_AUDIT_STATUS.SENT.toLowerCase():
            return 'success';
          case PROMO_CODE_AUDIT_STATUS.PROCESS.toLowerCase():
            return 'info';
          case PROMO_CODE_AUDIT_STATUS.FAILED.toLowerCase():
            return 'error';
          default:
            return 'default';
        }
      };
      return (
        <Chip
          label={value || 'N/A'}
          color={getStatusColor(value)}
          size="small"
          sx={{ fontWeight: 500 }}
        />
      );
    }
  },
  {
    field: "note",
    headerName: "Sent Via",
    flex: 0.8,
    minWidth: 100,
    renderCell: ({ value, row }) => {
      const getChipProps = (via) => {
        const v = (via || '').toLowerCase();
        if (v.includes('email')) {
          return { color: 'primary', icon: 'eva:email-fill', via: 'Email' };
        }
        if (v.includes('sms')) {
          return { color: 'info', icon: 'eva:message-square-fill', via: 'SMS' };
        }
        if (v.includes('whatsapp')) {
          return { color: 'success', icon: 'eva:phone-fill', via: 'WhatsApp' };
        }
        return { color: 'default', icon: 'eva:paper-plane-fill', via: v || 'N/A' };
      };
      const chipProps = getChipProps(value);
      return (
        <Chip
          label={chipProps.via || 'N/A'}
          color={chipProps.color}
          size="small"
          sx={{ fontWeight: 500 }}
        />
      );
    }
  },
  {
    field: "audit_created_at",
    headerName: "Sent Date",
    flex: 1,
    minWidth: 150,
    valueFormatter: ({ value }) => {
      return value ? fDateTime(value, dateFormat) : '-';
    },
  },
  {
    field: "valid_to",
    headerName: "Valid Till",
    flex: 1,
    minWidth: 150,
    valueFormatter: ({ value }) => {
      return value ? fDateTime(value, dateFormat) : '-';
    },
  },
    {
    field: "resend",
    headerName: "Resend",
    flex: 0.6,
    minWidth: 100,
    sortable: false,
    renderCell: ({ row }) => {
      if (row.audit_status?.toLowerCase() === PROMO_CODE_AUDIT_STATUS.FAILED.toLowerCase()) {
        return (
          <Chip
            label={
              <Typography variant="button" sx={{ color: '#fff' }}>Resend</Typography>
            }
            color="primary"
            clickable
            onClick={() => alert(row.value)}
            sx={{ background: '#1976d2', fontWeight: 500 }}
          />
        );
      }
      return null;
    }
  },
];

// Promo Codes Used by Customer columns
export const usedPromoCodesColumns = (isReadOnly, id) => [
  {
    field: "code",
    headerName: "Code",
    flex: 0.8,
    minWidth: 120,
    renderCell: ({ value }) => (
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#04aa58' }}>
        {value}
      </Typography>
    )
  },
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "discount_type",
    headerName: "Discount Type",
    flex: 0.8,
    minWidth: 120,
  },
  {
    field: "originalAmount",
    headerName: "Original Amount",
    flex: 1,
    minWidth: 130,
    renderCell: ({ row }) => (
      <Typography variant="body2" sx={{ fontWeight: 500, color: '#637381' }}>
        {row.usage_details?.initialCost}
      </Typography>
    )
  },
  {
    field: "discountApplied",
    headerName: "Discount Applied",
    flex: 1,
    minWidth: 140,
    renderCell: (params) => {
      const { value, row } = params;
      const suffix = row.discount_type === "PERCENTAGE" ? `${row.discount_value}%` : `${row.discount_value}`;
      return (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#00AB55' }}>
          {suffix}
        </Typography>
      );
    }
  },
  {
    field: "cost",
    headerName: "Final Amount",
    flex: 1,
    minWidth: 120,
    renderCell: ({ row }) => (
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#212B36' }}>
        {row.usage_details?.cost || ''}
      </Typography>
    )
  },
  {
    field: "audit_created_at",
    headerName: "Used Date",
    flex: 1.2,
    minWidth: 150,
    valueFormatter: ({ value }) => {
      return fDateTime(value, dateFormat);
    },
  },
  {
    field: "transactionId",
    headerName: "Transaction ID",
    flex: 1,
    minWidth: 120,
    renderCell: ({ value, row }) => {
      const transactionId = row?.usage_details?.transactionId || value;
      return (
        <Link underline="none" component={RouterLink} to={`/transactions/${Number(transactionId)}?source=cm-customer/${id}`}>
          {transactionId}
        </Link>
      );
    },
  },
];